/** Outcome of a single reacquire resolution attempt. */
export interface ReacquireOutcome<TDevice> {
  status: "bound" | "ambiguous" | "timeout";
  device?: TDevice;
  /** Browser handle that produced `device`; ownership moves to the old session. */
  usbDevice?: USBDevice;
  candidateCount: number;
}

export type ReleaseLease = () => void;

/**
 * Coordinates safe V2 (WorldCup/C003, no USB serial) re-enumeration across
 * multiple concurrent sessions.
 *
 * Two responsibilities:
 *  1. `acquireLease`/release — a single global exclusive lock so only one V2
 *     full-package flash (which resets BL1/SPL/TPL) is in flight at a time.
 *     Other V2 sessions can still run non-resetting partition flashes, and V3
 *     sessions are never blocked by this lease.
 *  2. `resolveCandidate` — after a reset, poll for the single new/unclaimed
 *     C003 handle that answers the expected protocol probe. Never guesses:
 *     zero candidates keeps polling until the deadline: two or more candidates
 *     fails immediately as "ambiguous" so the caller does not bind the wrong
 *     physical box.
 */
export class V2ReacquireCoordinator {
  private _leaseHeld = false;
  private readonly _leaseQueue: Array<() => void> = [];

  constructor(private readonly _isClaimed: (device: USBDevice) => boolean) {}

  /** Resolves once this caller holds the exclusive V2 full-package lease. */
  acquireLease(): Promise<ReleaseLease> {
    if (!this._leaseHeld) {
      this._leaseHeld = true;
      return Promise.resolve(() => this._release());
    }
    return new Promise<ReleaseLease>((resolve) => {
      this._leaseQueue.push(() => resolve(() => this._release()));
    });
  }

  private _release(): void {
    const next = this._leaseQueue.shift();
    if (next) next();
    else this._leaseHeld = false;
  }

  /**
   * Poll `navigator.usb.getDevices()` for VID 0x1b8e / PID 0xc003 handles not
   * already claimed by another live session, probing each candidate with
   * `probe`. Resolves as soon as exactly one candidate probes successfully.
   */
  async resolveCandidate<TDevice>(options: {
    usb: USB;
    deadlineMs: number;
    probe: (device: USBDevice) => Promise<TDevice | undefined>;
    /** Close a successful probe that cannot be bound safely. */
    dispose?: (device: TDevice) => Promise<void> | void;
    pollIntervalMs?: number;
    onPoll?: (candidateCount: number) => void;
  }): Promise<ReacquireOutcome<TDevice>> {
    const { usb, deadlineMs, probe, dispose, pollIntervalMs = 250, onPoll } = options;
    const deadline = Date.now() + deadlineMs;

    while (Date.now() < deadline) {
      const devices = await usb.getDevices().catch(() => [] as USBDevice[]);
      const raw = devices.filter((device) => device.vendorId === 0x1b8e && device.productId === 0xc003 && !this._isClaimed(device));
      onPoll?.(raw.length);

      const probeResults = await Promise.all(raw.map(async (usbDevice) => ({
        usbDevice,
        device: await probe(usbDevice),
      })));
      const bound = probeResults.filter(
        (result) => result.device !== undefined,
      ) as unknown as Array<{ usbDevice: USBDevice; device: TDevice }>;

      if (bound.length === 1) {
        return {
          status: "bound",
          device: bound[0].device,
          usbDevice: bound[0].usbDevice,
          candidateCount: 1,
        };
      }
      if (bound.length > 1) {
        if (dispose) {
          await Promise.allSettled(bound.map((result) => dispose(result.device)));
        }
        return { status: "ambiguous", candidateCount: bound.length };
      }

      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    return { status: "timeout", candidateCount: 0 };
  }
}
